import type { TsPoint } from "@/data/types";

/**
 * Unified time-series gap policy for every line chart in the operator
 * SPA. Solves the "missing data, no interpolation" complaint by
 * classifying each gap relative to the series' own median cadence and
 * either:
 *
 *   1. Sub-cadence jitter (gap ≤ 1.5 × cadence) — emit the points as-is.
 *      ECharts' `smooth: 0.x` blends the curve so noise reads as a
 *      continuous line without any extra work.
 *
 *   2. Short / medium gap (1.5 × cadence < gap ≤ outage threshold) —
 *      insert synthetic linearly-interpolated points at the median
 *      cadence. The line stays continuous and the operator sees a
 *      plausible bridge instead of a long straight segment between
 *      faraway endpoints. For step-style data (constant between
 *      samples) the bridge carries the last-known value forward.
 *
 *   3. Real outage (gap > outage threshold, or > absolute max bridge)
 *      — insert an explicit `null` one cadence step after the last
 *      good sample. Combined with `connectNulls: false` ECharts draws
 *      a clean break, so the operator can never misread a multi-hour
 *      device outage as a smooth interpolation along the baseline.
 *
 * Series-aware: the threshold is `outageFactor × cadence`, with an
 * absolute floor (`minOutageMs`) so high-frequency live streams aren't
 * broken by routine uplink jitter, and an absolute ceiling
 * (`maxBridgeMs`) so even slow bucket cadences eventually break for
 * truly long outages.
 *
 * Use [prepareLineSeries] for every line series the SPA renders; the
 * defaults are tuned for "looks great everywhere", and the policy is
 * tweakable per series when a particular signal (e.g. day-ahead price
 * step curve) wants different semantics.
 */

export type EChartsLinePoint = [number, number | null];

/**
 * One bridged gap: a stretch of the rendered line drawn continuous
 * between two REAL samples with no recorded data in between (case 2
 * above — synthetic samples or a straight chord). Real outages that
 * break the line are NOT listed. `startTs`/`endTs` are the timestamps
 * of the last real sample before and the first real sample after the
 * bridge, so everything strictly inside the span is invented data.
 */
export interface BridgedSpan {
  startTs: number;
  endTs: number;
}

/** Return shape of the `…WithSpans` preparers (HIST-14): the ECharts
 *  data plus the bridged-gap metadata charts need to mark interpolated
 *  stretches honestly (markArea + tooltip suffix). */
export interface PreparedLine {
  points: EChartsLinePoint[];
  bridgedSpans: BridgedSpan[];
}

export interface GapPolicy {
  /**
   * How many `cadence` steps before a gap is considered an outage and
   * the line is broken. The smaller, the stricter. Default 8 — i.e.
   * one missed sample bridges fine, eight missed in a row breaks.
   */
  outageFactor?: number;
  /**
   * Absolute floor for the outage threshold (ms). High-frequency data
   * (e.g. 1 Hz live stream) gets a generous absolute floor so a 30 s
   * uplink hiccup never reads as an outage. Default 30 min.
   */
  minOutageMs?: number;
  /**
   * Absolute ceiling for the bridge: past this, always break — even if
   * `outageFactor × cadence` is bigger. Caps the visual range of
   * synthetic samples so a day-cadence series doesn't try to bridge a
   * three-day outage. Default 4 hours.
   */
  maxBridgeMs?: number;
  /**
   * When true (default), insert synthetic interpolated samples at the
   * median cadence across medium gaps so the rendered line stays
   * smooth. When false, just emit the original points — ECharts will
   * draw a straight line between the endpoints (useful for low-density
   * series where intermediate samples would look fake).
   */
  insertSamples?: boolean;
  /**
   * For step-style data (constant between samples — e.g. day-ahead
   * price, controller setpoint), copy the previous value forward
   * across the bridge instead of interpolating linearly.
   */
  step?: boolean;
}

const DEFAULT_OUTAGE_FACTOR = 8;
const DEFAULT_MIN_OUTAGE_MS = 30 * 60 * 1000;
const DEFAULT_MAX_BRIDGE_MS = 4 * 60 * 60 * 1000;
/** Sub-cadence threshold: gaps below this don't need any synthetic
 *  samples, ECharts smoothing handles them. */
const SUB_CADENCE_MULT = 1.5;

/** Median of the positive inter-sample deltas in `pts` (ms). 0 if < 2 pts. */
export function medianCadenceMs(pts: ReadonlyArray<{ ts: number }>): number {
  if (pts.length < 2) return 0;
  const deltas: number[] = [];
  for (let i = 1; i < pts.length; i++) {
    const d = pts[i].ts - pts[i - 1].ts;
    if (d > 0) deltas.push(d);
  }
  if (!deltas.length) return 0;
  deltas.sort((a, b) => a - b);
  const mid = deltas.length >> 1;
  return deltas.length % 2 ? deltas[mid] : (deltas[mid - 1] + deltas[mid]) / 2;
}

/**
 * Transform a finite-valued time-series into the `[ts, value | null][]`
 * shape ECharts wants, with consistent gap handling per the policy
 * above. The series MUST be sorted by ts ascending (the SPA's adapters
 * already guarantee this).
 *
 * Interpolation across medium gaps uses monotone-cubic Hermite (PCHIP)
 * by default — it honours the local slope on both sides of the gap so
 * a bridge across (e.g.) a rising ramp looks like a continuation of
 * the ramp instead of a straight chord. PCHIP is "shape preserving":
 * it never overshoots the surrounding samples, so we don't introduce
 * fake peaks or troughs that the operator might misread. At the very
 * first/last gap we fall back to linear (no neighbour on one side).
 */
export function prepareLineSeries(
  pts: ReadonlyArray<{ ts: number; value: number | null | undefined }>,
  opts: GapPolicy = {},
): EChartsLinePoint[] {
  return prepareLineSeriesWithSpans(pts, opts).points;
}

/**
 * Same gap policy as [prepareLineSeries], but ALSO returns the bridged
 * spans (HIST-14) so chart layers can mark every invented stretch —
 * one span per medium gap drawn continuous, covering both the
 * synthetic-sample bridges and the `insertSamples: false` straight
 * chords (both render data where none was recorded).
 */
export function prepareLineSeriesWithSpans(
  pts: ReadonlyArray<{ ts: number; value: number | null | undefined }>,
  opts: GapPolicy = {},
): PreparedLine {
  const bridgedSpans: BridgedSpan[] = [];
  if (pts.length === 0) return { points: [], bridgedSpans };
  // Filter to finite samples — NaN / null / undefined are pre-existing
  // holes and we'll create explicit breaks for any that fall outside
  // the bridge window.
  const finite: TsPoint[] = [];
  for (const p of pts) {
    if (Number.isFinite(p.value as number)) {
      finite.push({ ts: p.ts, value: p.value as number });
    }
  }
  if (finite.length === 0) return { points: [], bridgedSpans };
  if (finite.length === 1) {
    return { points: [[finite[0].ts, finite[0].value]], bridgedSpans };
  }

  const cadence = medianCadenceMs(finite);
  const outageFactor = opts.outageFactor ?? DEFAULT_OUTAGE_FACTOR;
  const minOutageMs = opts.minOutageMs ?? DEFAULT_MIN_OUTAGE_MS;
  const maxBridgeMs = opts.maxBridgeMs ?? DEFAULT_MAX_BRIDGE_MS;
  const insertSamples = opts.insertSamples ?? true;
  const step = opts.step ?? false;

  // Outage threshold: whichever is larger of (factor × cadence) and the
  // absolute floor. With no cadence info (single point), only the
  // absolute floor applies.
  const outageThreshold = Math.max(
    cadence > 0 ? cadence * outageFactor : 0,
    minOutageMs,
  );

  // Pre-compute PCHIP slopes at every finite sample so the bridge can
  // ask for an interpolated value at an arbitrary `t` between samples
  // i and i+1 in O(1). Slopes are weighted-harmonic-mean of the
  // surrounding linear slopes; this is the Fritsch-Carlson rule that
  // makes PCHIP monotone (no spurious wiggles).
  const slopes = step ? null : pchipSlopes(finite);

  const out: EChartsLinePoint[] = [];
  for (let i = 0; i < finite.length; i++) {
    const cur = finite[i];
    out.push([cur.ts, cur.value]);
    const next = finite[i + 1];
    if (!next) continue;
    const gap = next.ts - cur.ts;
    if (cadence <= 0 || gap <= cadence * SUB_CADENCE_MULT) continue;
    // Outage — clean break.
    if (gap > outageThreshold || gap > maxBridgeMs) {
      const breakTs = cur.ts + Math.min(cadence, gap / 2);
      out.push([breakTs, null]);
      continue;
    }
    // Medium gap — drawn continuous over no data, so record the span
    // for the honest-marking layers (HIST-14) whether the bridge is
    // synthetic samples or a plain chord.
    bridgedSpans.push({ startTs: cur.ts, endTs: next.ts });
    // Insert synthetic samples so the line stays smooth.
    if (!insertSamples) continue;
    const stepCount = Math.max(0, Math.floor(gap / cadence) - 1);
    for (let k = 1; k <= stepCount; k++) {
      const t = cur.ts + k * cadence;
      let v: number;
      if (step) {
        v = cur.value;
      } else if (slopes) {
        v = pchipInterp(cur.ts, cur.value, next.ts, next.value, slopes[i], slopes[i + 1], t);
      } else {
        v = cur.value + (next.value - cur.value) * (k / (stepCount + 1));
      }
      out.push([t, v]);
    }
  }
  return { points: out, bridgedSpans };
}

/**
 * Pre-shaped data (already `[ts, value][]`) preparer — accepts the
 * tuple form some chart components already produce internally so they
 * can be migrated to the unified policy in a single line.
 */
export function prepareLineTuples(
  tuples: ReadonlyArray<readonly [number, number]>,
  opts: GapPolicy = {},
): EChartsLinePoint[] {
  return prepareLineSeries(
    tuples.map(([ts, value]) => ({ ts, value })),
    opts,
  );
}

/** Tuple-input twin of [prepareLineSeriesWithSpans]. */
export function prepareLineTuplesWithSpans(
  tuples: ReadonlyArray<readonly [number, number]>,
  opts: GapPolicy = {},
): PreparedLine {
  return prepareLineSeriesWithSpans(
    tuples.map(([ts, value]) => ({ ts, value })),
    opts,
  );
}

/**
 * Fill short null runs in a categorical/hour-indexed array (e.g. the
 * 24-element day-ahead curve) with linear interpolation between the
 * surrounding non-null endpoints. Long runs are left untouched so a
 * real "missing several hours" gap still reads as a break under
 * `connectNulls: false`. Leading/trailing nulls are preserved.
 *
 * `maxRun` is the maximum number of consecutive nulls we'll bridge
 * (default 2 — i.e. a single hour missing in a day-ahead curve fills,
 * a long sub-day outage stays broken).
 */
export function bridgeShortNullRuns(
  arr: ReadonlyArray<number | null | undefined>,
  maxRun = 2,
): (number | null)[] {
  const n = arr.length;
  if (n === 0) return [];
  const out: (number | null)[] = arr.map((v) =>
    Number.isFinite(v as number) ? (v as number) : null,
  );
  let i = 0;
  while (i < n) {
    if (out[i] !== null) { i++; continue; }
    // Run of nulls starting at i.
    let j = i;
    while (j < n && out[j] === null) j++;
    const left = i - 1;
    const right = j;
    const runLen = j - i;
    // Only bridge interior runs with both endpoints present and within
    // the maxRun budget.
    if (left >= 0 && right < n && runLen <= maxRun) {
      const a = out[left] as number;
      const b = out[right] as number;
      for (let k = 0; k < runLen; k++) {
        const t = (k + 1) / (runLen + 1);
        out[i + k] = a + (b - a) * t;
      }
    }
    i = j;
  }
  return out;
}

// ---------------------------------------------------------------------------
// PCHIP (monotone-cubic Hermite) helpers
// ---------------------------------------------------------------------------
//
// PCHIP picks tangent slopes at each sample so the interpolant never
// overshoots the surrounding data — a property we want here because
// chart bridges must not invent excursions that the operator might
// read as real signal. Implementation follows Fritsch & Carlson (1980)
// + the SciPy `pchip_interpolate` rules: zero slope when adjacent
// linear slopes change sign; harmonic mean otherwise. Linear is the
// degenerate case at the endpoints.

function pchipSlopes(pts: ReadonlyArray<TsPoint>): number[] {
  const n = pts.length;
  if (n === 0) return [];
  if (n === 1) return [0];
  const m: number[] = new Array(n);
  const dx: number[] = new Array(n - 1);
  const slope: number[] = new Array(n - 1);
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1].ts - pts[i].ts;
    slope[i] = dx[i] > 0 ? (pts[i + 1].value - pts[i].value) / dx[i] : 0;
  }
  // Endpoint slopes: one-sided three-point formula, then clamp so the
  // sign matches the adjacent secant (prevents overshoot).
  m[0] = endpointSlope(slope[0], slope[1] ?? slope[0], dx[0], dx[1] ?? dx[0]);
  m[n - 1] = endpointSlope(
    slope[n - 2],
    slope[n - 3] ?? slope[n - 2],
    dx[n - 2],
    dx[n - 3] ?? dx[n - 2],
  );
  for (let i = 1; i < n - 1; i++) {
    const s0 = slope[i - 1];
    const s1 = slope[i];
    if (s0 === 0 || s1 === 0 || s0 * s1 < 0) {
      m[i] = 0;
    } else {
      // Weighted harmonic mean (Fritsch-Carlson).
      const w1 = 2 * dx[i] + dx[i - 1];
      const w2 = dx[i] + 2 * dx[i - 1];
      m[i] = (w1 + w2) / (w1 / s0 + w2 / s1);
    }
  }
  return m;
}

function endpointSlope(s0: number, s1: number, h0: number, h1: number): number {
  // Three-point one-sided estimate.
  const m = ((2 * h0 + h1) * s0 - h0 * s1) / (h0 + h1 || 1);
  // Clamp to avoid overshoot at the boundary: signs must match s0, and
  // magnitude must not exceed 3× |s0| (also Fritsch-Carlson).
  if (m * s0 <= 0) return 0;
  if (s0 * s1 <= 0 && Math.abs(m) > 3 * Math.abs(s0)) return 3 * s0;
  return m;
}

function pchipInterp(
  x0: number, y0: number,
  x1: number, y1: number,
  m0: number, m1: number,
  x: number,
): number {
  const h = x1 - x0;
  if (h <= 0) return y0;
  const t = (x - x0) / h;
  const t2 = t * t;
  const t3 = t2 * t;
  const h00 = 2 * t3 - 3 * t2 + 1;
  const h10 = t3 - 2 * t2 + t;
  const h01 = -2 * t3 + 3 * t2;
  const h11 = t3 - t2;
  return h00 * y0 + h10 * h * m0 + h01 * y1 + h11 * h * m1;
}
